import React, { useState, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Tooltip, Link, CircularProgress } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import { useThemeMode } from '../theme/ThemeContext';

const supportsZip = typeof CompressionStream !== 'undefined';

/**
 * Top bar for the review page when user is logged in.
 * Matches the tenant's header style (logo, name, theme toggle, Back to home, email, Logout).
 */
export default function ReviewPageHeader({ tenantConfig, currentUser, onLogout, adminView = false, onAdminViewToggle, onDownloadAll = null, downloading = false }) {
  const { themeMode, toggleTheme } = useThemeMode();
  const [copied, setCopied] = useState(false);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 2,
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <img
          src={tenantConfig.logo}
          alt={`${tenantConfig.name} Logo`}
          width={tenantConfig.logoWidth || 48}
          height={tenantConfig.logoHeight || 48}
        />
        <Typography variant="h6" component="h1">
          {tenantConfig.name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {onAdminViewToggle != null && (
          <Tooltip title={adminView ? 'Switch to review mode' : 'Switch to admin mode'}>
            <IconButton onClick={() => onAdminViewToggle(!adminView)} size="small">
              {adminView ? <AdminPanelSettingsIcon /> : <AdminPanelSettingsOutlinedIcon />}
            </IconButton>
          </Tooltip>
        )}
        {supportsZip && currentUser && onDownloadAll && (
          <Tooltip title="Download all files as ZIP">
            <span>
              <IconButton onClick={onDownloadAll} size="small" disabled={downloading}>
                {downloading
                  ? <CircularProgress size={18} color="inherit" />
                  : <DownloadIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={copied ? 'Copied!' : 'Copy review link'}>
          <IconButton onClick={handleCopyLink} size="small">
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={themeMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
          <IconButton onClick={toggleTheme} size="small">
            {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </IconButton>
        </Tooltip>
        <Button component={Link} href="/" variant="text" size="small">
          Back to home
        </Button>
        <Typography variant="body2" color="text.secondary">
          {currentUser?.email}
        </Typography>
        <Button variant="outlined" size="small" onClick={onLogout}>
          Logout
        </Button>
      </Box>
    </Box>
  );
}
